import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RestaurantService } from '../../../services/restaurant/restaurant.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnyIntegrationNew, Provider } from '../../../interfaces/restaurant';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

type CredentialControlValue = string | boolean | null;

type CredentialsForm = Record<string, FormControl<CredentialControlValue>>;

type AddIntegrationForm = {
  provider: FormControl<Provider | ''>;
  credentials: FormGroup<CredentialsForm>;
  proxy?: FormControl<boolean>;
  proxyUnifiedMode?: FormControl<boolean>;
};

type AddIntegrationFormValue = ReturnType<FormGroup<AddIntegrationForm>['getRawValue']>;

type CredentialControlConfig = {
  value: CredentialControlValue;
  validators?: ValidatorFn | ValidatorFn[];
};

@UntilDestroy()
@Component({
  selector: 'app-add-integration',
  templateUrl: './add-integration.component.html',
  styleUrls: ['./add-integration.scss']
})
export class AddIntegrationComponent {
  loading = false;
  public restaurantId: number;
  public addIntegration = this.generateForm();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<any>,
    public notifier: NotifierService,
    private translate: TranslateService,
    private restaurantService: RestaurantService
  ) {
    this.restaurantId = this.data.restaurantId;
    this.addIntegration.get('provider')?.valueChanges
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(val => {
        if (!val) {
          return;
        }
        this.addControl(val);
      });
  }

  private generateForm(): FormGroup<AddIntegrationForm> {
    return new FormGroup<AddIntegrationForm>({
      provider: new FormControl<Provider | ''>('', { nonNullable: true, validators: [Validators.required] }),
      credentials: new FormGroup<CredentialsForm>({})
    });
  }

  get credentialsFormGroup(): FormGroup<CredentialsForm> {
    return this.addIntegration.controls.credentials;
  }

  public addControl(val: Provider): void {
    const urlRegex = new RegExp(
      '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR IP (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~[\\]+=\\-/,]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', // fragment locator
      'i'
    );

    const credentialsControlsMap: Record<Provider, Record<string, CredentialControlConfig>> = {
      VAST: {
        vastTagUrl: { value: '', validators: [Validators.required, Validators.pattern(urlRegex)] }
      },
      vistarMedia: {
        apiKey: { value: '', validators: [Validators.required, Validators.maxLength(255)] },
        networkId: { value: '', validators: [Validators.required, Validators.maxLength(255)] },
        venueId: { value: '', validators: [Validators.required, Validators.maxLength(255)] }
      },
      kelseysMenu: {
        storeNumber: { value: '', validators: [Validators.required, Validators.pattern('^[0-9]*$'), Validators.maxLength(255)] }
      },
      bostonPizzaUI: {
        controlMusicUrl: { value: '', validators: [Validators.pattern(urlRegex)] },
        googleReviewsUrl: { value: '', validators: [Validators.pattern(urlRegex)] },
        guestSurveyUrl: { value: '', validators: [Validators.pattern(urlRegex)] },
        notAcceptingRewardsCardsDisclaimer: { value: false },
        happyHourPilot: { value: false },
        interacBanner: { value: false }
      },
      bostonPizzaEventsAPI: {
        chapterId: { value: '', validators: [Validators.required, Validators.maxLength(255)] },
        chapterLocation: { value: '', validators: [Validators.required, Validators.maxLength(255)] },
        chapterTitle: { value: '', validators: [Validators.required, Validators.maxLength(255)] },
        slug: { value: '', validators: [Validators.required, Validators.maxLength(255)] }
      },
      foyr: {
        restaurantId: { value: '', validators: [Validators.required, Validators.maxLength(255)]}
      }
    };

    const addProxyControls = () => {

      if (!this.addIntegration.contains('proxy')) {
        this.addIntegration.addControl(
          'proxy',
          new FormControl(false, { nonNullable: true, validators: [Validators.required] })
        );
      }

      if (!this.addIntegration.contains('proxyUnifiedMode')) {
        this.addIntegration.addControl(
          'proxyUnifiedMode',
          new FormControl({ value: false, disabled: true }, { nonNullable: true, validators: [Validators.required] })
        );
      }

      this.addIntegration.controls.proxy?.valueChanges.pipe(
        untilDestroyed(this)
      ).subscribe((proxyValue: boolean) => {
        const proxyUnifiedModeControl = this.addIntegration.controls.proxyUnifiedMode;
        if (!proxyValue) {
          proxyUnifiedModeControl?.disable({ emitEvent: false });
          proxyUnifiedModeControl?.setValue(false, { emitEvent: false });
        } else {
          proxyUnifiedModeControl?.enable({ emitEvent: false });
        }
      });
    };

    const credentialsForm = this.credentialsFormGroup as FormGroup;

    Object.keys(credentialsForm.controls).forEach(controlName => {
      credentialsForm.removeControl(controlName);
    });

    const currentControls = credentialsControlsMap[val] || {};

    Object.entries(currentControls).forEach(([key, config]) => {
      credentialsForm.addControl(
        key,
        new FormControl<CredentialControlValue>(config.value, config.validators || [])
      );
    });

    addProxyControls();
  }

  public addNewIntegration(): void {
    this.loading = true;

    const integration = this.buildIntegration(this.addIntegration.getRawValue());

    if (!integration) {
      this.loading = false;
      throw new Error('Unknown provider');
    }

    this.restaurantService.addIntegration(this.restaurantId, integration)
      .subscribe(
        (data) => {
          this.loading = false;
          this.dialogRef.close(data);
          this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.addedIntegration'));
        },
        () => {
          this.loading = false;
        }
      );
  }

  private buildIntegration(value: AddIntegrationFormValue): AnyIntegrationNew | null {
    const proxy = value.proxy ?? false;
    const proxyUnifiedMode = value.proxyUnifiedMode ?? false;
    const credentials = value.credentials;

    switch (value.provider) {
      case 'VAST':
        return {
          provider: 'VAST',
          credentials: {
            vastTagUrl: credentials.vastTagUrl as string,
          },
          proxy,
          proxyUnifiedMode,
        };
      case 'vistarMedia':
        return {
          provider: 'vistarMedia',
          credentials: {
            apiKey: credentials.apiKey as string,
            networkId: credentials.networkId as string,
            venueId: credentials.venueId as string,
          },
          proxy,
          proxyUnifiedMode,
        };
      case 'kelseysMenu':
        return {
          provider: 'kelseysMenu',
          credentials: {
            storeNumber: credentials.storeNumber as string,
          },
          proxy,
          proxyUnifiedMode,
        };
      case 'bostonPizzaUI':
        return {
          provider: 'bostonPizzaUI',
          credentials: {
            controlMusicUrl: credentials.controlMusicUrl as string,
            googleReviewsUrl: credentials.googleReviewsUrl as string,
            guestSurveyUrl: credentials.guestSurveyUrl as string,
            notAcceptingRewardsCardsDisclaimer: credentials.notAcceptingRewardsCardsDisclaimer as boolean,
            happyHourPilot: credentials.happyHourPilot as boolean,
            interacBanner: credentials.interacBanner as boolean,
          },
          proxy,
          proxyUnifiedMode,
        };
      case 'bostonPizzaEventsAPI':
        return {
          provider: 'bostonPizzaEventsAPI',
          credentials: {
            chapterId: credentials.chapterId as string,
            chapterLocation: credentials.chapterLocation as string,
            chapterTitle: credentials.chapterTitle as string,
            slug: credentials.slug as string,
          },
          proxy,
          proxyUnifiedMode,
        };
      case 'foyr':
        return {
          provider: 'foyr',
          credentials: {
            restaurantId: credentials.restaurantId as string
          },
          proxy,
          proxyUnifiedMode,
        };
      default:
        return null;
    }
  }
}
